/**
 * Cliente 360 — os seis blocos e a timeline unificada (H08).
 *
 * ── Uma tela, seis blocos, seis carimbos de frescor ───────────────────────
 *
 * O contrato decide bloco a bloco o que é fresco e o que é projeção, pela
 * volatilidade: pessoa, oportunidades e tarefas vêm frescas do Twenty; empresa,
 * conversas e timeline vêm de projeção. Cada bloco carrega o próprio `stale`
 * porque cada um degrada sozinho — Twenty fora deixa pessoa e oportunidades
 * defasadas e não encosta nas conversas.
 *
 * A tela reflete isso: a faixa de defasagem é POR BLOCO. Uma faixa no topo
 * diria que a tela inteira está velha quando só metade está, e o vendedor
 * desconfiaria do que está certo.
 *
 * ── A timeline é o que dá sentido ao resto ────────────────────────────────
 *
 * *"Mensagem, mudança de etapa, tarefa e evento de portal na mesma linha do
 * tempo."* A armadilha é separar por abas: o valor está em ver que a mensagem
 * veio depois da mudança de etapa e antes da tarefa. Aqui o filtro por tipo é
 * subtrativo — tira ruído de uma sequência que continua sendo sequência.
 *
 * ── O que esta tela NÃO faz, e por quê ───────────────────────────────────
 *
 * **Origem acumulada** (de qual portal o cliente veio) não existe: é a E07t, e
 * conector de portal é a etapa E. Nenhum campo do contrato carrega isso.
 *
 * **Edição propagada** não existe: não há rota de escrita de cliente no `/v1`.
 * Um formulário que não tem para onde enviar seria pior que a ausência dele.
 *
 * As duas estão na task e ficam declaradas como lacuna, com dono.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Cliente360, TipoDeItemDaTimeline } from '@cliqcrm/contracts';
import { useObservarRecurso } from '@/shared/api/assinaturas';
import { useApi, useCamposPersonalizados, useCliente360 } from '@/shared/api/consultas';
import { ValoresDosCampos } from '@/entities/campos/campo-personalizado';
import { chaves } from '@/shared/api/chaves';
import { useInvalidar } from '@/shared/api/provedor';
import { EditorDeEtiquetas } from '@/features/atendimento/editar-etiquetas';
import { nomeDoCanal } from '@/entities/atendimento/canal';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import { useTimelineDoCliente } from '../model/usar-timeline';
import { Etiquetas } from '@/entities/atendimento/etiqueta';
import './cliente-360.css';
import { Avatar, Botao, CabecalhoDeTela, Ficha, Icone } from '@/shared/ui';
import { NovaTarefa } from '@/features/comercial/criar-tarefa';
import { OportunidadeNo360 } from './oportunidade-no-360';
import { CadastroEmEdicao } from './cadastro';
import { FichaDoPacienteNoCliente } from './bloco-da-ficha';
import { ConveniosNoCliente } from './bloco-do-convenio';
import { DocumentosNoCliente } from './aba-dos-documentos';
import { ConsultaNoCliente } from './aba-da-consulta';
import { Bloco } from './bloco';
import { PacienteNoCabecalho } from './paciente-no-cabecalho';
import { ExamesNoCliente } from './aba-dos-exames';
import { TratamentosNoCliente } from './aba-do-tratamento';
import { ProntuarioNoCliente } from './aba-do-prontuario';
import { usePode, usePreferencias, useSessao } from '@/shared/sessao/contexto';
import { PacienteNoSisac } from '@/features/saude/vincular-paciente';
import { pacienteNoSisacLigado } from '@/entities/saude/paciente';
import { LinhaDoTempo } from './linha-do-tempo';
import { PainelDeConversas } from './painel-de-conversas';
import { PainelDeNegocios } from './painel-de-negocios';
import { PainelDeTarefas } from './painel-de-tarefas';

type Aba =
  | 'negocios'
  | 'tarefas'
  | 'conversas'
  | 'tempo'
  | 'cadastro'
  | 'consulta'
  | 'documentos'
  | 'exames'
  | 'tratamento'
  | 'prontuario';
/**
 * As cinco abas, com a contagem que cada uma sabe dar.
 *
 * A contagem entra no rótulo porque é metade da resposta: "Tarefas 2" já diz
 * se vale abrir. `null` onde não há o que contar — a linha do tempo pagina, e
 * um número parcial ali diria menos que nenhum.
 */
const ABAS: readonly {
  chave: Aba;
  nome: string;
  contagem: (d: Cliente360 | undefined) => number | null;
}[] = [
  { chave: 'negocios', nome: 'Oportunidades', contagem: (d) => d?.oportunidades.total ?? null },
  { chave: 'tarefas', nome: 'Tarefas', contagem: (d) => d?.tarefas.total ?? null },
  { chave: 'conversas', nome: 'Conversas', contagem: (d) => d?.conversas.total ?? null },
  { chave: 'tempo', nome: 'Linha do tempo', contagem: () => null },
  { chave: 'cadastro', nome: 'Cadastro', contagem: () => null },
];
/**
 * A clínica (#811): as abas da consulta, dos documentos, dos exames e do
 * tratamento, só com ela ligada, depois do cadastro.
 *
 * Sem contagem, de propósito: contar pediria a lista inteira a cada vez que o
 * 360 abre, e cada leitura de consulta, documento e exame entra no registro de
 * acesso. Ler para contar seria registrar uma leitura que ninguém fez.
 */
const ABAS_DA_CLINICA: typeof ABAS = [
  { chave: 'consulta', nome: 'Consulta', contagem: () => null },
  { chave: 'documentos', nome: 'Documentos', contagem: () => null },
  { chave: 'exames', nome: 'Exames', contagem: () => null },
  { chave: 'tratamento', nome: 'Tratamento', contagem: () => null },
];

/** O prontuário (ADR-0022): só para quem tem a marca de acesso, depois do tratamento. */
const ABA_DO_PRONTUARIO: (typeof ABAS)[number] = {
  chave: 'prontuario',
  nome: 'Prontuário',
  contagem: () => null,
};
/** O que cada aba mostra. Some no celular: a aba escolhida já nomeia o assunto. */
const APOIO: Record<Aba, string> = {
  negocios: 'O que está em jogo com esta pessoa, e em que etapa cada oportunidade está.',
  tarefas: 'O que alguém precisa fazer com este cliente, e até quando.',
  conversas: 'Por onde se falou com ele, e quando foi a última vez.',
  tempo: 'Tudo que aconteceu, na ordem — mensagens, mudanças de etapa e tarefas.',
  cadastro: 'Os dados da pessoa, e de onde ela veio. Cada bloco se edita no lugar.',
  consulta: 'A consulta de cada oportunidade do cliente, e a marcação dela no SISAC.',
  documentos:
    'Identidade, carteirinha, termos e contratos. Separados dos arquivos de cada oportunidade.',
  exames: 'Os pedidos de exame, com as etapas de cada um e quem deu o ok em cada etapa.',
  tratamento:
    'O tratamento fechado com o paciente: o valor de tabela do SISAC e o valor negociado.',
  prontuario: 'O resumo de cada atendimento. O prontuário completo fica no sistema da clínica.',
};

export function ClienteTrezentosESessenta({
  clienteId,
  aoAbrirConversa,
  aoVoltar,
}: {
  clienteId: string;
  aoAbrirConversa: (conversaId: string) => void;
  aoVoltar: () => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const { estado } = useSessao();
  const editaOportunidade = usePode('oportunidade', 'editar');
  /** A clínica (#811): a ficha do paciente só existe com ela ligada. */
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);
  const { clinica: agendaDaClinica } = usePreferencias();
  // O paciente no SISAC (#1024): só com a busca e a leitura do paciente ligadas.
  const comPacienteNoSisac = clinica && pacienteNoSisacLigado(agendaDaClinica);
  /** O prontuário (ADR-0022): a clínica ligada E a marca por pessoa. */
  const prontuario =
    clinica && estado.nome === 'autenticado' && (estado.me.acesso_ao_prontuario ?? false);
  const podeEditar = usePode('cliente', 'editar');

  /*
   * Grava as etiquetas próprias do cliente.
   *
   * Revalida só o 360: a LISTA de clientes não passa pelo cache de consultas
   * — `usar-lista-de-clientes` tem carregamento próprio —, então não há chave
   * para invalidar ali. Ela relê ao ser montada de novo.
   */
  const definirEtiquetas = async (etiquetas: readonly string[]): Promise<unknown> => {
    if (!api) throw new Error('Sem sessão.');
    const resposta = await api.definirEtiquetasDoCliente({
      caminho: { id: clienteId },
      corpo: { etiquetas: [...etiquetas] },
    });
    invalidar([chaves.cliente360(clienteId)]);
    return resposta;
  };

  const consulta = useCliente360(clienteId);
  // Os campos personalizados do cliente (#568), para ler na aba Cadastro.
  const camposDoCliente = useCamposPersonalizados('cliente');
  const [tipos, definirTipos] = useState<readonly TipoDeItemDaTimeline[]>([]);
  /*
   * CINCO abas, as mesmas no mouse e no dedo.
   *
   * Antes eram duas colunas no desktop e três abas abaixo de 720px — dois
   * desenhos para a mesma tela, e a linha do tempo por último em ambos. Cada
   * aba agora é uma pergunta fechada: "o que está em jogo", "o que eu devo
   * fazer", "o que aconteceu".
   */
  const [aba, definirAba] = useState<Aba>('negocios');
  const [editandoCadastro, definirEditandoCadastro] = useState(false);
  const [recado, definirRecado] = useState<string | null>(null);
  // O diálogo some no sucesso, e sumir calado deixa quem clicou sem saber se
  // funcionou. O recado é a confirmação, e vive onde já vivia.
  const [criandoTarefa, definirCriandoTarefa] = useState(false);
  /** A oportunidade aberta no card grande (28/09/2026), pelo id canônico. */
  const [oportunidadeAberta, definirOportunidadeAberta] = useState<string | null>(null);
  const barraDasAbas = useRef<HTMLElement>(null);

  /*
   * A aba escolhida é trazida para a VISTA na barra que rola.
   *
   * Em 390px as cinco abas não cabem: tocar em "Cadastro" trocaria o painel e
   * deixaria a aba escolhida fora da tela, com a barra mostrando três abas e
   * nenhuma marcada.
   *
   * `scrollLeft`, e nunca `scrollIntoView`: este rola o ancestral junto, e o
   * cabeçalho — que é fixo de propósito — sairia do lugar.
   */
  useEffect(() => {
    const barra = barraDasAbas.current;
    const ativa = barra?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!barra || !ativa) return;
    barra.scrollLeft = Math.max(0, ativa.offsetLeft - (barra.clientWidth - ativa.offsetWidth) / 2);
  }, [aba]);

  const dados = consulta.data;

  const timeline = useTimelineDoCliente(
    clienteId,
    tipos,
    dados
      ? {
          itens: dados.timeline.itens,
          cursor: dados.timeline.cursor_proximo,
          temMais: dados.timeline.tem_mais,
        }
      : null,
  );

  // Tempo real: a conversa aberta com mais atividade não é conhecida aqui, mas
  // as oportunidades são — e mudança de etapa é o que mais muda esta tela.
  const assinatura = dados?.oportunidades.itens.find((o) => o.id)?.id ?? null;
  useObservarRecurso(assinatura ? `opportunity:${assinatura}` : null);

  /*
   * A conversa que "Abrir conversa" abre é a MAIS RECENTE.
   *
   * O bloco de conversas vem ordenado do servidor, e a primeira é a de agora —
   * que é a que se quer continuar. Abrir a mais antiga faria o botão parecer
   * quebrado num cliente com histórico.
   */
  const primeiraConversa = dados?.conversas.itens[0]?.id ?? null;

  if (consulta.isPending) {
    return (
      <div className="c360">
        <Carregando forma="ficha" linhas={6} rotulo="Carregando o cliente" />
      </div>
    );
  }

  // Só sem nada na tela: com dados carregados, uma atualização que falhou
  // (o limite de requisições, a rede) não apaga o que estava certo.
  if (consulta.error && consulta.data === undefined) {
    return (
      <div className="c360 c360--centro">
        <Erro
          erro={consulta.error}
          aoRepetir={() => void consulta.refetch()}
          titulo="Não foi possível abrir este cliente"
        />
      </div>
    );
  }

  if (!dados) return <div className="c360" />;

  return (
    <div className="c360">
      {/*
        O cabeçalho DEIXA AGIR, e é fixo.

        Ele tinha nome, subtítulo e um "voltar" — nenhum botão numa tela que o
        vendedor abre justamente para dar o próximo passo, e com o telefone
        enterrado na ficha de cadastro do outro lado.

        Fixo porque a linha do tempo rola por dezenas de eventos: sem isso, o
        botão do próximo passo rola embora junto com o nome de quem ele é.
      */}
      <CabecalhoDeTela
        /*
          O telefone é um ícone ao lado do nome: tocar liga, e o número aparece
          ao passar o mouse. Escrito por extenso, ele ocupava uma fileira só
          para si debaixo do cabeçalho.
        */
        aoLadoDoTitulo={
          dados.cliente.telefone_exibicao ? (
            <a
              className="c360__ligar"
              href={`tel:${dados.cliente.telefone_exibicao}`}
              title={dados.cliente.telefone_exibicao}
              aria-label={`Ligar para ${dados.cliente.telefone_exibicao}`}
            >
              <Icone nome="telefone" />
            </a>
          ) : undefined
        }
        variante="identidade"
        titulo={dados.cliente.nome_exibicao ?? 'Cliente sem nome'}
        voltar={
          /*
            Voltar é ÍCONE, e não "← Voltar" escrito.

            Ele é cromo permanente numa fileira que também carrega a ação
            principal da tela: em texto, disputa leitura com "Abrir conversa"
            logo ao lado. O nome vai no `aria-label`, que é onde ele serve.
          */
          <span className="cab__voltar">
            <Botao variante="fantasma" icone aria-label="Voltar" title="Voltar" onClick={aoVoltar}>
              <Icone nome="voltar" />
            </Botao>
          </span>
        }
        retrato={
          <span className="cab__retrato">
            <Avatar nome={dados.cliente.nome_exibicao} semente={dados.cliente.id} tamanho={48} />
          </span>
        }
        meta={
          <>
            {[dados.cliente.cargo, dados.cliente.cidade].filter(Boolean).map((f) => (
              <span key={f}>{f}</span>
            ))}
            {dados.cliente.criado_em && (
              <span>
                cliente desde{' '}
                <b>
                  {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium' }).format(
                    new Date(dados.cliente.criado_em),
                  )}
                </b>
              </span>
            )}
            {dados.cliente.origem && (
              <span>
                veio de <b>{nomeDoCanal(dados.cliente.origem.canal) ?? 'canal desconhecido'}</b>
              </span>
            )}
            {clinica && <PacienteNoCabecalho clienteId={clienteId} />}
            {/*
              As etiquetas de TODAS as conversas dele, somadas e ordenadas por
              frequência — a que aparece em três de quatro diz mais sobre o
              cliente do que a que apareceu uma vez.

              Aqui elas ficam ABERTAS, por regra de tela: a bandeira fechada é
              certa na linha da fila, onde disputa espaço com a prévia e o
              relógio. Nesta tela ela é identidade, e fechada obrigaria a passar
              o mouse para saber quem é a pessoa que a tela inteira descreve.
            */}
            {/*
              Duas origens, e a diferença fica VISÍVEL.

              As de cima são do cliente — postas à mão, e as únicas que se
              editam. As de baixo são reflexo das conversas. Juntas, o `x` de
              remover teria de aparecer nas duas, e tirar uma de conversa não
              faria nada: a próxima projeção a traria de volta. Botão que
              desfaz sozinho o que a pessoa acabou de fazer é pior que botão
              ausente.
            */}
            <span className="c360__etiquetas">
              <Etiquetas titulos={dados.cliente.etiquetas_proprias ?? []} miuda />
              <EditorDeEtiquetas
                aplicadas={dados.cliente.etiquetas_proprias ?? []}
                oQue="deste cliente"
                aoDefinir={definirEtiquetas}
              />
            </span>
            {(dados.cliente.etiquetas ?? []).length > 0 && (
              <span className="c360__etiquetas">
                <span className="c360__etiquetas-origem">das conversas</span>
                <Etiquetas titulos={dados.cliente.etiquetas ?? []} miuda />
              </span>
            )}
          </>
        }
        acoes={
          /*
            Duas ações, e não três. O ⋯ do desenho ficou de fora porque não há o
            que pôr nele: transferir responsável e unir cadastros são de outras
            telas, e o cliente do 360 não carrega responsável no contrato. Um
            menu que abre vazio é pior que menu nenhum.
          */
          <>
            <Botao
              variante="primario"
              desabilitado={primeiraConversa === null}
              motivo="Este cliente ainda não tem conversa registrada."
              onClick={() => primeiraConversa && aoAbrirConversa(primeiraConversa)}
            >
              Abrir conversa
            </Botao>
            {/*
              Abre o formulário com o cliente JÁ preenchido — o vínculo vem
              daqui porque criar solta e vincular depois é o caminho que ninguém
              completa.
            */}
            <Botao variante="secundario" onClick={() => definirCriandoTarefa(true)}>
              Nova tarefa
            </Botao>
          </>
        }
        extra={
          /*
            O e-mail é o começo de muito do que se faz aqui, e estava na terceira
            ficha da coluna de consulta. `mailto:` porque quem clica quer
            escrever, não copiar. O telefone foi para o ícone ao lado do nome.
          */
          dados.cliente.email ? (
            <a href={`mailto:${dados.cliente.email}`}>{dados.cliente.email}</a>
          ) : undefined
        }
      />

      {criandoTarefa && (
        <NovaTarefa
          vinculo={{
            alvo: { tipo: 'cliente', id: clienteId },
            rotulo: dados.cliente.nome_exibicao ?? 'este cliente',
          }}
          deOnde="Criada no Cliente 360"
          aoCriar={() => definirRecado('Tarefa criada.')}
          aoFechar={() => definirCriandoTarefa(false)}
        />
      )}

      {oportunidadeAberta && (
        <OportunidadeNo360
          oportunidadeId={oportunidadeAberta}
          aoAbrirConversa={aoAbrirConversa}
          aoFechar={() => definirOportunidadeAberta(null)}
        />
      )}

      {recado && (
        <p className="c360__recado" role="status">
          {recado}
        </p>
      )}

      {/*
        A barra de abas. Não é o `shared/ui/etapas` porque aqui cada aba carrega a
        CONTAGEM — "Tarefas 2" já diz se vale abrir, e é metade da resposta.

        A barra rola de lado em 390px, e a aba escolhida é trazida para a vista
        por `scrollLeft` — nunca `scrollIntoView`, que rola o ancestral junto e
        tiraria do lugar o cabeçalho, que é fixo de propósito.
      */}
      <nav className="c360__abas" ref={barraDasAbas}>
        {/*
          O trilho interno existe para as abas se ALINHAREM com o painel.

          O painel é centrado num teto de 1100px; a barra atravessa a tela
          inteira, porque é o fio dela que separa o cabeçalho do corpo. Sem o
          trilho, a marca da aba ativa ficava na ponta esquerda e a tabela
          começava trezentos pixels adiante — duas coisas que se explicam uma à
          outra, desalinhadas.
        */}
        <div className="c360__abas-trilho" role="tablist" aria-label="O que ver deste cliente">
          {(clinica
            ? [...ABAS, ...ABAS_DA_CLINICA, ...(prontuario ? [ABA_DO_PRONTUARIO] : [])]
            : ABAS
          ).map(({ chave, nome, contagem }) => (
            <button
              key={chave}
              type="button"
              role="tab"
              id={`c360-aba-${chave}`}
              aria-selected={aba === chave}
              aria-controls="c360-painel"
              className="c360__aba"
              onClick={() => definirAba(chave)}
            >
              {nome}
              {contagem(dados) !== null && (
                <span className="c360__aba-contagem">{contagem(dados)}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <div
        className="c360__corpo"
        id="c360-painel"
        role="tabpanel"
        aria-labelledby={`c360-aba-${aba}`}
      >
        <div className="c360__painel">
          <div className="c360__painel-topo">
            <p className="c360__painel-apoio">{APOIO[aba]}</p>
          </div>

          {aba === 'negocios' && (
            <PainelDeNegocios lista={dados.oportunidades} aoAbrir={definirOportunidadeAberta} />
          )}

          {aba === 'tarefas' && <PainelDeTarefas lista={dados.tarefas} />}

          {aba === 'consulta' && clinica && (
            <ConsultaNoCliente
              oportunidades={dados.oportunidades.itens}
              podeEditar={editaOportunidade}
              contato={{
                nome: dados.cliente.nome_exibicao,
                telefone: dados.cliente.telefone_exibicao,
                email: dados.cliente.email,
              }}
            />
          )}

          {aba === 'documentos' && clinica && (
            <DocumentosNoCliente clienteId={clienteId} podeEditar={podeEditar} />
          )}

          {aba === 'tratamento' && clinica && (
            <TratamentosNoCliente
              clienteId={clienteId}
              podeEditar={podeEditar}
              oportunidades={dados.oportunidades.itens}
            />
          )}

          {aba === 'prontuario' && prontuario && <ProntuarioNoCliente clienteId={clienteId} />}

          {aba === 'exames' && clinica && (
            <ExamesNoCliente clienteId={clienteId} podeEditar={podeEditar} />
          )}

          {aba === 'conversas' && (
            <PainelDeConversas lista={dados.conversas} aoAbrir={aoAbrirConversa} />
          )}

          {aba === 'tempo' && (
            <LinhaDoTempo
              timeline={timeline}
              cobertura={dados.timeline.cobertura}
              frescor={dados.timeline.frescor}
              tipos={tipos}
              aoFiltrar={definirTipos}
              aoAbrirConversa={aoAbrirConversa}
            />
          )}

          {aba === 'cadastro' && (
            <>
              {/*
                O formulário do cadastro aparecia DUAS vezes (até 16/10/2026):
                acima da grade e dentro do bloco, com dois "Editar cadastro"
                abrindo os dois. Ficou só o de dentro do bloco, com a ação no
                título dele, como os blocos da clínica.
              */}
              <div className="c360__consulta">
                <Bloco
                  titulo="Cadastro"
                  frescor={dados.cliente.frescor}
                  largo={editandoCadastro}
                  acao={
                    !editandoCadastro && (
                      <Botao
                        miudo
                        variante="fantasma"
                        aria-label="Editar cadastro"
                        onClick={() => definirEditandoCadastro(true)}
                      >
                        Editar
                      </Botao>
                    )
                  }
                >
                  {editandoCadastro ? (
                    <CadastroEmEdicao
                      clienteId={clienteId}
                      cliente={dados.cliente}
                      aoFechar={() => definirEditandoCadastro(false)}
                    />
                  ) : null}
                  <Ficha
                    larguraRotulo="110px"
                    linhas={[
                      /*
                       * E-mail e telefone somem da ficha enquanto o formulário está
                       * aberto: mostrar o campo duas vezes, um editável e outro não,
                       * faria a pessoa não saber qual vale. Documento e "cliente
                       * desde" ficam — eles não se editam aqui.
                       */
                      ...(editandoCadastro
                        ? []
                        : [
                            { rotulo: 'E-mail', valor: dados.cliente.email ?? '—' },
                            { rotulo: 'Telefone', valor: dados.cliente.telefone_exibicao ?? '—' },
                          ]),
                      {
                        rotulo: 'Documento',
                        // Só a existência: ver o número exige o step-up da B17, e esta
                        // tela abre dezenas de vezes por dia.
                        valor: dados.cliente.documento.presente
                          ? 'cadastrado · exige confirmação para ver'
                          : 'não informado',
                        protegido: dados.cliente.documento.presente,
                      },
                      {
                        rotulo: 'Cliente desde',
                        valor: dados.cliente.criado_em
                          ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium' }).format(
                              new Date(dados.cliente.criado_em),
                            )
                          : '—',
                      },
                      /*
                  Por onde ele CHEGOU — o canal da primeira conversa.

                  Não é "por onde ele fala hoje": origem não muda, e quem chegou
                  pelo WhatsApp e hoje fala pelo Instagram continua tendo vindo do
                  WhatsApp. Misturar as duas faria a contagem de leads por origem
                  mudar sozinha de uma semana para a outra.

                  Sem conversa nenhuma, dizer isso é melhor que um traço: "não
                  conversou por canal nenhum" é uma informação sobre o cliente —
                  provavelmente veio de importação ou de conector.
                */
                      dados.cliente.origem === null
                        ? {
                            rotulo: 'Veio de',
                            valor: 'ainda não conversou por canal nenhum',
                            vazio: true,
                          }
                        : {
                            rotulo: 'Veio de',
                            valor: (
                              <>
                                {nomeDoCanal(dados.cliente.origem.canal) ?? 'canal desconhecido'}
                                {dados.cliente.origem.em && (
                                  <span className="ficha__apoio">
                                    {' · '}
                                    {new Intl.DateTimeFormat('pt-BR', {
                                      dateStyle: 'medium',
                                    }).format(new Date(dados.cliente.origem.em))}
                                  </span>
                                )}
                              </>
                            ),
                          },
                      /*
                       * Na clínica a empresa é uma linha do cadastro, e não um
                       * bloco: quase todo paciente é pessoa física, e um bloco
                       * inteiro para dizer isso empurrava a ficha para baixo.
                       */
                      ...(clinica
                        ? [
                            dados.empresa
                              ? {
                                  rotulo: 'Empresa',
                                  valor: dados.empresa.nome ?? 'sem nome',
                                }
                              : {
                                  rotulo: 'Empresa',
                                  valor: 'pessoa física, sem empresa vinculada',
                                  vazio: true,
                                },
                          ]
                        : []),
                    ]}
                  />
                  {/*
                    Os campos personalizados (#568), embaixo dos de sempre. Somem
                    enquanto o formulário está aberto, como o e-mail e o
                    telefone: as entradas deles estão lá.
                  */}
                  {!editandoCadastro && (
                    <ValoresDosCampos
                      campos={camposDoCliente.data?.itens ?? []}
                      valores={dados.cliente.campos}
                    />
                  )}
                </Bloco>

                {!clinica && (
                  <Bloco titulo="Empresa" frescor={dados.empresa?.frescor}>
                    {dados.empresa ? (
                      <Ficha
                        larguraRotulo="110px"
                        linhas={[
                          { rotulo: 'Nome', valor: dados.empresa.nome ?? '—' },
                          { rotulo: 'Domínio', valor: dados.empresa.dominio ?? '—' },
                          { rotulo: 'Cidade', valor: dados.empresa.cidade ?? '—' },
                        ]}
                      />
                    ) : (
                      /*
                       * Ausência de empresa não é falta de dado — o contrato
                       * registra a pessoa física como tal.
                       *
                       * A frase dizia "é o caso mais comum numa revenda". Era um
                       * fato do mercado de veículos, e trocar a palavra não o
                       * salvaria: numa clínica ele nem é verdade. Some a oração.
                       */
                      <p className="c360__nota">Pessoa física, sem empresa vinculada.</p>
                    )}
                  </Bloco>
                )}

                {/* A clínica (#811): a ficha em dois blocos e o convênio entre eles. */}
                {clinica && (
                  <FichaDoPacienteNoCliente
                    clienteId={clienteId}
                    podeEditar={podeEditar}
                    parte="pessoa"
                  />
                )}
                {clinica && <ConveniosNoCliente clienteId={clienteId} podeEditar={podeEditar} />}
                {comPacienteNoSisac && (
                  <Bloco titulo="Paciente no SISAC">
                    <PacienteNoSisac
                      key={clienteId}
                      clienteId={clienteId}
                      contato={{
                        nome: dados.cliente.nome_exibicao,
                        telefone: dados.cliente.telefone_exibicao,
                        email: dados.cliente.email,
                      }}
                      podeEditar={podeEditar}
                    />
                  </Bloco>
                )}
                {clinica && (
                  <FichaDoPacienteNoCliente
                    clienteId={clienteId}
                    podeEditar={podeEditar}
                    parte="contato"
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
